/**
 * Sources for the home hero's background loop — one per theme.
 *
 * Self-hosted rather than hotlinked, and each held at its source resolution.
 *
 *   dark   Pexels 36841642  1920×1080 @24fps   168.5MB -> 7.2MB
 *   light  Pexels 31723676  3840×2160 @30fps     9.9MB -> 5.4MB
 *
 * The dark clip is 1080p because that is the highest rendition Pexels holds
 * for it — the API lists 1920×1080, 1280×720, 960×540 and 640×360 and nothing
 * above. It is already at source resolution, not downscaled.
 *
 * Both are re-encoded H.264 at CRF 26 with no audio track. The dark source ran
 * 120 seconds at 11Mbps; a hero loop nobody watches deliberately does not need
 * two minutes, so it is trimmed to the first 20 to match the light clip.
 *
 * The 4K light clip is 5.4MB only because smooth grey gradients compress well.
 * Decoding 3840×2160 is still real work on a mid-range phone, and the hero
 * renders at most ~1440 CSS px wide — if the homepage's LCP regresses, the
 * 2560×1440 or 1920×1080 rendition of the same clip is the first thing to try.
 *
 * Posters are the first frame of each encode, scaled to 1920, so there is no
 * visible jump when the video takes over from the still.
 *
 * Either URL may be an `.m3u8` instead of an `.mp4` — `BackgroundVideo` picks
 * its playback path from the extension and loads hls.js only when it finds a
 * manifest. That is the swap for putting these on a streaming host later.
 */
export interface HeroVideoSource {
  /** `.mp4` for a progressive file, `.m3u8` for an HLS manifest. */
  readonly src: string;
  /** First frame of `src`. Shown until playback starts, and if it never does. */
  readonly poster: string;
}

export const HERO_VIDEO_DARK: HeroVideoSource = {
  src: import.meta.env.VITE_HERO_VIDEO_DARK_URL ?? "/video/hero-dark.mp4",
  poster: "/video/hero-dark-poster.jpg",
};

export const HERO_VIDEO_LIGHT: HeroVideoSource = {
  src: import.meta.env.VITE_HERO_VIDEO_LIGHT_URL ?? "/video/hero-light.mp4",
  poster: "/video/hero-light-poster.jpg",
};

export function heroVideoFor(isDark: boolean): HeroVideoSource {
  return isDark ? HERO_VIDEO_DARK : HERO_VIDEO_LIGHT;
}

/** True when the URL needs hls.js rather than a plain `src` assignment. */
export function isHlsManifest(url: string): boolean {
  return url.split("?")[0].endsWith(".m3u8");
}
